import type { ReactNode } from 'react';
import { Label } from '@primer/react';

import type { BoardAgentLabelView } from '../../client/types.js';

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/u;

/**
 * Returns the more legible of black and white for a valid label background.
 * Invalid wire data deliberately falls back to Primer's default Label colors.
 */
export function agentLabelForeground(color: string): '#000000' | '#ffffff' | null {
  if (!HEX_COLOR.test(color)) return null;

  const channels = [1, 3, 5].map((offset) => Number.parseInt(color.slice(offset, offset + 2), 16) / 255);
  const luminance = channels.reduce(
    (total, channel, index) =>
      total + [0.2126, 0.7152, 0.0722][index]! * (channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4),
    0,
  );
  const blackContrast = (luminance + 0.05) / 0.05;
  const whiteContrast = 1.05 / (luminance + 0.05);
  return blackContrast >= whiteContrast ? '#000000' : '#ffffff';
}

/** Shared compact-card and detail-card presentation for agent-controlled metadata. */
export function AgentCardLabels({
  labels,
  compact = false,
}: {
  readonly labels: readonly BoardAgentLabelView[];
  readonly compact?: boolean;
}): ReactNode {
  if (labels.length === 0) return null;

  return (
    <ul
      className={compact ? 'agent-card-labels agent-card-labels-compact' : 'agent-card-labels'}
      role="list"
      aria-label="Agent labels"
    >
      {labels.map((label) => {
        const foreground = agentLabelForeground(label.color);
        return (
          <li key={label.key}>
            <Label
              className="agent-card-label"
              style={foreground === null ? undefined : { backgroundColor: label.color, color: foreground }}
            >
              {label.text}
            </Label>
          </li>
        );
      })}
    </ul>
  );
}
